import { Trans, useTranslation } from '@repo/translations/use-translation';
import { lazy, Suspense } from 'react';

import { TextBadge } from '@zooplus/ui-components';

const BasicCard = lazy(() => import('./basic-card-icon.svg'));
const PremiumCard = lazy(() => import('./premium-card-icon.svg'));
import { MembershipsComparison } from '../../../../hooks/useMembershipsComparison';
import { OfferPrice } from '../../../UI/OfferPrice';
import { ProgramLogo } from '../../../UI/ProgramLogo/ProgramLogo';

import styles from './ProgramPlanHeader.module.css';

export interface ProgramPlanHeaderProps {
  membershipsComparison: MembershipsComparison;
  plan: string;
}

export const ProgramPlanHeader = ({ membershipsComparison, plan }: ProgramPlanHeaderProps) => {
  const { t } = useTranslation();

  const isCurrentPlan = () => {
    if (membershipsComparison.customerLoyaltyLevelType === plan) {
      return true;
    } else {
      return (
        membershipsComparison.customerLoyaltyLevelType === 'SP_MIGRATION' && plan === 'PREMIUM'
      );
    }
  };

  return (
    <div className={styles.planHeader}>
      <div className={styles.logoAndPlanContainer}>
        <Suspense fallback={null}>
          <ProgramLogo height={19} />
        </Suspense>
        <h2 className={styles.plan}>
          {plan === 'BASIC'
            ? t('loyalty-program.public-landing-page.program-plans.header.basic-level')
            : t('loyalty-program.public-landing-page.program-plans.header.premium-level')}
        </h2>
      </div>
      <Suspense fallback={null}>
        {plan === 'BASIC' ? (
          <BasicCard
            data-testid={'basic-card'}
            className={styles.card}
            title="Basic Loyalty Program Card icon"
          />
        ) : (
          <PremiumCard
            data-testid={'premium-card'}
            className={styles.card}
            title="Premium Loyalty Program Card icon"
          />
        )}
      </Suspense>
      <div className={styles.priceContainer}>
        {plan === 'BASIC' ? (
          <span className={styles.price}>
            {t('loyalty-program.public-landing-page.program-plans.header.level-price-free')}
          </span>
        ) : (
          plan !== 'BASIC' && (
            <span data-testid={'price-per-year'} className={styles.yearlyPrice}>
              <Trans
                i18nKey={'loyalty-program.public-landing-page.program-plans.header.yearly-price'}
                components={{
                  Price: (
                    <OfferPrice
                      price={
                        membershipsComparison.premiumLoyaltyMembershipInfo.membershipPrice?.price
                      }
                      referencePrice={
                        membershipsComparison.premiumLoyaltyMembershipInfo.membershipPrice
                          ?.referencePrice
                      }
                      secondaryCurrencyPrice={
                        membershipsComparison.premiumLoyaltyMembershipInfo.membershipPrice
                          ?.secondaryCurrencyPrice
                      }
                      size="large"
                    />
                  ),
                }}
              />
            </span>
          )
        )}
        {isCurrentPlan() && (
          <TextBadge
            className={styles.currentPlan}
            label={t('loyalty-program.public-landing-page.program-plans.current-plan')}
            variant="info"
          />
        )}
      </div>
    </div>
  );
};
